import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { motion, MotionConfig } from "framer-motion";
import { Loader2 } from "lucide-react";
import { ThemeProvider } from "@/lib/theme";
import { authApi } from "@/lib/api";
import { CommandPalette } from "@/components/CommandPalette";
import { ClarityAnalytics } from "@/components/ClarityAnalytics";
import { applyCurrentSiteBrandMetadata } from "@/lib/siteBrand";

// Route-level code splitting. Each page becomes its own chunk, so the first
// paint no longer ships all 34 pages (plus recharts and react-big-calendar,
// which only Analytics and Calendar need) inside one 472KB bundle. lazyNamed
// adapts our named page exports to the default-export shape React.lazy wants.
// The import() argument stays a literal string so the bundler can still split.
const lazyNamed = (factory, name) => lazy(() => factory().then((m) => ({ default: m[name] })));

const Login = lazyNamed(() => import("@/pages/Auth"), "Login");
const Register = lazyNamed(() => import("@/pages/Auth"), "Register");
const Activate = lazyNamed(() => import("@/pages/Auth"), "Activate");
const Landing = lazyNamed(() => import("@/pages/Landing"), "Landing");
const Partner = lazyNamed(() => import("@/pages/Partner"), "Partner");
const Privacy = lazyNamed(() => import("@/pages/Legal"), "Privacy");
const Terms = lazyNamed(() => import("@/pages/Legal"), "Terms");
const DataDeletion = lazyNamed(() => import("@/pages/Legal"), "DataDeletion");
const BillingSuccess = lazyNamed(() => import("@/pages/Billing"), "BillingSuccess");
const BillingCancelled = lazyNamed(() => import("@/pages/Billing"), "BillingCancelled");
const BillingSettings = lazyNamed(() => import("@/pages/Billing"), "BillingSettings");
const Affiliates = lazyNamed(() => import("@/pages/Affiliates"), "Affiliates");
const Seller = lazyNamed(() => import("@/pages/Seller"), "Seller");
const Workspaces = lazyNamed(() => import("@/pages/Workspaces"), "Workspaces");
const Today = lazyNamed(() => import("@/pages/Today"), "Today");
const Launch = lazyNamed(() => import("@/pages/Launch"), "Launch");
const Dashboard = lazyNamed(() => import("@/pages/Dashboard"), "Dashboard");
const Strategy = lazyNamed(() => import("@/pages/Strategy"), "Strategy");
const CalendarPage = lazyNamed(() => import("@/pages/Calendar"), "CalendarPage");
const ReelBuilder = lazyNamed(() => import("@/pages/ReelBuilder"), "ReelBuilder");
const Coach = lazyNamed(() => import("@/pages/Coach"), "Coach");
const MyDay = lazyNamed(() => import("@/pages/MyDay"), "MyDay");
const Cockpit = lazyNamed(() => import("@/pages/Cockpit"), "Cockpit");
const IdeasBank = lazyNamed(() => import("@/pages/IdeasBank"), "IdeasBank");
const Modules = lazyNamed(() => import("@/pages/Modules"), "Modules");
const Connections = lazyNamed(() => import("@/pages/Connections"), "Connections");
const AIBrain = lazyNamed(() => import("@/pages/AIBrain"), "AIBrain");
const Memory = lazyNamed(() => import("@/pages/Memory"), "Memory");
const Profile = lazyNamed(() => import("@/pages/Profile"), "Profile");
const KeysVault = lazyNamed(() => import("@/pages/KeysVault"), "KeysVault");
const GrowthPlanner = lazyNamed(() => import("@/pages/GrowthPlanner"), "GrowthPlanner");
const Department = lazyNamed(() => import("@/pages/Department"), "Department");
const Settings = lazyNamed(() => import("@/pages/Settings"), "Settings");
const Composer = lazyNamed(() => import("@/pages/Composer"), "Composer");
const BlogWriter = lazyNamed(() => import("@/pages/BlogWriter"), "BlogWriter");
const ArticleReview = lazyNamed(() => import("@/pages/ArticleReview"), "ArticleReview");
const Admin = lazyNamed(() => import("@/pages/Admin"), "Admin");
const Analytics = lazyNamed(() => import("@/pages/Analytics"), "Analytics");
const UgcStudio = lazyNamed(() => import("@/pages/UgcStudio"), "UgcStudio");

const ProtectedRoute = ({ children }) => {
  if (!authApi.isAuthenticated()) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  if (authApi.isAuthenticated()) return <Navigate to="/workspaces" replace />;
  return children;
};

// Shown for the split second a not-yet-loaded page chunk takes to fetch. Kept
// minimal so it never flashes heavy chrome; after the first visit the chunk is
// cached and this never appears again for that route.
const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="w-6 h-6 animate-spin text-primary" />
  </div>
);

// Enter transition: fast, subtle fade + slide.
const PAGE_TRANSITION = { duration: 0.18, ease: 'easeOut' };

const SiteMetadata = () => {
  const location = useLocation();
  useEffect(() => { applyCurrentSiteBrandMetadata(); }, [location.pathname]);
  return null;
};

// One page is mounted at a time, keyed by path, so a fresh motion.div replays
// the enter fade on every navigation. We deliberately do NOT wrap this in
// AnimatePresence mode="wait": combined with React.lazy + Suspense it
// deadlocks when the entering chunk is already cached (the exiting page never
// fires onExitComplete, so the new page never mounts and navigation looks
// frozen). Dropping the exit animation removes that failure mode entirely and
// keeps only one page in the DOM at a time -- no duplicate data-testids, no
// layout jump. Suspense sits above the motion.div to catch lazy chunk loads.
const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <Suspense fallback={<RouteFallback />}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={PAGE_TRANSITION}
      >
        <Routes location={location}>
          <Route path="/" element={<Landing />} />
          <Route path="/partners" element={<Partner />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/activate" element={<PublicRoute><Activate /></PublicRoute>} />
          {/* Stripe Checkout return URLs: no auth guard, the user may return
              in a fresh tab. The webhook owns the actual subscription state. */}
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/billing/cancelled" element={<BillingCancelled />} />
          <Route path="/review/article/:articleId" element={<ArticleReview />} />
          <Route path="/billing" element={<ProtectedRoute><BillingSettings /></ProtectedRoute>} />
          <Route path="/affiliates" element={<ProtectedRoute><Affiliates /></ProtectedRoute>} />
          <Route path="/seller" element={<ProtectedRoute><Seller /></ProtectedRoute>} />
          <Route path="/workspaces" element={<ProtectedRoute><Workspaces /></ProtectedRoute>} />
          <Route path="/today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
          <Route path="/launch" element={<ProtectedRoute><Launch /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/strategy" element={<ProtectedRoute><Strategy /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/coach" element={<ProtectedRoute><Coach /></ProtectedRoute>} />
          <Route path="/myday" element={<ProtectedRoute><MyDay /></ProtectedRoute>} />
          <Route path="/cockpit" element={<ProtectedRoute><Cockpit /></ProtectedRoute>} />
          <Route path="/compose" element={<ProtectedRoute><Composer /></ProtectedRoute>} />
          <Route path="/blog" element={<ProtectedRoute><BlogWriter /></ProtectedRoute>} />
          <Route path="/ugc" element={<ProtectedRoute><UgcStudio /></ProtectedRoute>} />
          <Route path="/ideas" element={<ProtectedRoute><IdeasBank /></ProtectedRoute>} />
          <Route path="/modules" element={<ProtectedRoute><Modules /></ProtectedRoute>} />
          <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
          <Route path="/ai-brain" element={<ProtectedRoute><AIBrain /></ProtectedRoute>} />
          <Route path="/memory" element={<ProtectedRoute><Memory /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/keys" element={<ProtectedRoute><KeysVault /></ProtectedRoute>} />
          <Route path="/growth" element={<ProtectedRoute><GrowthPlanner /></ProtectedRoute>} />
          <Route path="/department" element={<ProtectedRoute><Department /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/reel/:id" element={<ProtectedRoute><ReelBuilder /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </motion.div>
    </Suspense>
  );
};

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <ThemeProvider>
        <BrowserRouter>
          <SiteMetadata />
          <ClarityAnalytics />
          <Toaster position="top-right" richColors closeButton />
          <CommandPalette />
          <AnimatedRoutes />
        </BrowserRouter>
      </ThemeProvider>
    </MotionConfig>
  );
}

export default App;
